import { useEffect, useState } from "react";

const frameCache = new Map<string, HTMLImageElement>();

async function loadFrameImage(url: string): Promise<HTMLImageElement | null> {
	if (frameCache.has(url)) {
		return frameCache.get(url)!;
	}

	const needsProxy = url.includes("bloblandingpageprd.blob.core.windows.net");
	const fetchUrl = needsProxy
		? `/api/proxy-image?url=${encodeURIComponent(url)}`
		: url;

	try {
		const response = await fetch(fetchUrl);
		if (!response.ok) return null;

		const blob = await response.blob();
		const blobUrl = URL.createObjectURL(blob);

		return await new Promise((resolve) => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.onload = () => {
				frameCache.set(url, img);
				URL.revokeObjectURL(blobUrl);
				resolve(img);
			};
			img.onerror = () => {
				URL.revokeObjectURL(blobUrl);
				resolve(null);
			};
			img.src = blobUrl;
		});
	} catch {
		return null;
	}
}

export function useCarrosselFrames(frameUrls: string[]) {
	const [frames, setFrames] = useState<HTMLImageElement[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!frameUrls.length) {
			setFrames([]);
			setIsLoading(false);
			return;
		}

		let cancelled = false;
		setIsLoading(true);

		Promise.all(frameUrls.map((url) => loadFrameImage(url))).then((images) => {
			if (cancelled) return;
			setFrames(images.filter((img): img is HTMLImageElement => img !== null));
			setIsLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [frameUrls.join("|")]);

	return { frames, isLoading };
}
